import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ContentCard, Button, Badge } from '@dopomogai/ui';
import { Github, Zap, Bot, Globe, Gauge, HeartHandshake } from 'lucide-react';

type Accent = 'violet' | 'cyan' | 'blue' | 'emerald' | 'slate' | 'amber' | 'rose' | 'indigo' | 'purple' | 'teal' | 'orange' | 'lime' | 'fuchsia' | 'pink' | 'gray';

type WhyUsItem = {
  icon?: React.ReactNode;
  title?: string;
  titleKey?: string;
  text?: string | React.ReactNode;
  textKey?: string;
  cta?: { label: string; href?: string; onClick?: () => void };
};

type Stat = {
  value: string;
  label: string;
  accent?: Accent; // NEW: color accent to match hero pills
  valueKey?: string;
  labelKey?: string;
};

export type WhyUsSectionProps = {
  eyebrow?: string;
  eyebrowKey?: string;
  title?: string;
  titleKey?: string;
  subtitle?: string;
  subtitleKey?: string;
  items: WhyUsItem[];
  stats?: Stat[];
  columns?: 2 | 3 | 4;
  className?: string;
  cta?: { label: string; href?: string; onClick?: () => void };
};

const accentClasses = (accent?: Accent) => {
  switch (accent) {
    case 'violet': return { bg: 'bg-violet-50', text: 'text-violet-700', stripe: 'bg-violet-400' };
    case 'cyan': return { bg: 'bg-cyan-50', text: 'text-cyan-700', stripe: 'bg-cyan-400' };
    case 'blue': return { bg: 'bg-blue-50', text: 'text-blue-700', stripe: 'bg-blue-400' };
    case 'emerald': return { bg: 'bg-emerald-50', text: 'text-emerald-700', stripe: 'bg-emerald-400' };
    case 'amber': return { bg: 'bg-amber-50', text: 'text-amber-700', stripe: 'bg-amber-400' };
    case 'rose': return { bg: 'bg-rose-50', text: 'text-rose-700', stripe: 'bg-rose-400' };
    case 'indigo': return { bg: 'bg-indigo-50', text: 'text-indigo-700', stripe: 'bg-indigo-400' };
    case 'purple': return { bg: 'bg-purple-50', text: 'text-purple-700', stripe: 'bg-purple-400' };
    case 'teal': return { bg: 'bg-teal-50', text: 'text-teal-700', stripe: 'bg-teal-400' };
    case 'orange': return { bg: 'bg-orange-50', text: 'text-orange-700', stripe: 'bg-orange-400' };
    case 'lime': return { bg: 'bg-lime-50', text: 'text-lime-700', stripe: 'bg-lime-400' };
    case 'fuchsia': return { bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', stripe: 'bg-fuchsia-400' };
    case 'pink': return { bg: 'bg-pink-50', text: 'text-pink-700', stripe: 'bg-pink-400' };
    case 'slate': return { bg: 'bg-slate-50', text: 'text-slate-800', stripe: 'bg-slate-400' };
    case 'gray': return { bg: 'bg-gray-50', text: 'text-gray-700', stripe: 'bg-gray-400' };
    default: return { bg: 'bg-white', text: 'text-slate-900', stripe: 'bg-slate-300' };
  }
};

export const WhyUsSection: React.FC<WhyUsSectionProps> = ({
  eyebrow,
  eyebrowKey,
  title,
  titleKey,
  subtitle,
  subtitleKey,
  items,
  stats,
  columns = 3,
  className,
  cta,
}) => {
  const { t } = useTranslation('homepage');
  const resolvedEyebrow = eyebrowKey ? t(eyebrowKey) : eyebrow;
  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedSubtitle = subtitleKey ? t(subtitleKey) : subtitle;

  const columnClasses = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
  }[columns];

  return (
    <section className={clsx('bg-white', className)}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="text-center max-w-3xl mx-auto">
          {resolvedEyebrow && (
            <div className="inline-block mb-2">
              <Badge accentColor="green"text={resolvedEyebrow}></Badge>
            </div>
          )}
          {resolvedTitle && (
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">
              {resolvedTitle}
            </h2>
          )}
          {resolvedSubtitle && (
            <p className="mt-3 text-slate-700 text-base md:text-lg">
              {resolvedSubtitle}
            </p>
          )}
        </div>

        {stats && stats.length > 0 && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {stats.map((s, i) => {
              const c = accentClasses(s.accent);
              return (
                <div
                  key={i}
                  className={clsx(
                    'relative border-2 border-black rounded-xl px-4 py-3 text-center transition-all duration-200',
                    'hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]',
                    c.bg
                  )}
                >
                  <div aria-hidden="true" className={clsx('absolute inset-x-2 top-2 h-2 rounded-full', c.stripe)} />
                  <div className={clsx('text-xl md:text-2xl font-extrabold', c.text)}>
                    {s.valueKey ? t(s.valueKey) : s.value}
                  </div>
                  <div className="text-sm text-slate-700">
                    {s.labelKey ? t(s.labelKey) : s.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className={clsx('mt-8 grid gap-5 md:gap-6', columnClasses)}>
          {items.map((item, idx) => (
            <ContentCard
              key={idx}
              icon={item.icon}
              title={item.titleKey ? t(item.titleKey) : (item.title ?? '')}
              text={item.textKey ? t(item.textKey) : (item.text ?? '')}
              index={idx}
              cta={
                item.cta
                  ? {
                      text: item.cta.label,
                      href: item.cta.href,
                      onClick: item.cta.onClick,
                      variant: 'secondary',
                    }
                  : undefined
              }
            />
          ))}
        </div>

        {cta && (
          <div className="mt-8 md:mt-10 text-center">
            {cta.href ? (
              <Button
                variant="primary"
                onClick={cta.onClick}
                asChild
              >
                <a href={cta.href}>{cta.label}</a>
              </Button>
            ) : (
              <Button
                variant="primary"
                text={cta.label}
                onClick={cta.onClick}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default WhyUsSection;

export const defaultWhyUsData = {
  eyebrow: 'Why teams choose Dopomogai',
  title: 'Built to ship faster—with control that lasts',
  subtitle:
    '90% prebuilt, open‑source core, and agents that evolve under your approval. Global from day one; ethical by design.',
  stats: [
    { value: '90%', label: 'prebuilt modules', accent: 'violet' },
    { value: '3–5 days', label: 'basic setup', accent: 'blue' },
    { value: '100+', label: 'languages', accent: 'cyan' },
    { value: '33%', label: 'to non‑profits', accent: 'emerald' },
  ],
  items: [
    { icon: <Zap className="text-emerald-600" />, title: 'Build faster', text: '90% prebuilt modules—ship in days, not months.' },
    { icon: <Github className="text-emerald-600" />, title: 'Open‑source core', text: 'Transparent, modular builder. No lock‑in.' },
    { icon: <Bot className="text-emerald-600" />, title: 'Under your control', text: 'Self‑evolving meta‑agents with approvals and audit trails.' },
    { icon: <Globe className="text-emerald-600" />, title: 'Global by default', text: '100+ languages; web, social, and phone; human handover.' },
    { icon: <Gauge className="text-emerald-600" />, title: 'Transparent costs', text: 'One‑time onboarding; usage via credits. No support retainer.' },
    { icon: <HeartHandshake className="text-emerald-600" />, title: 'Impact‑first', text: '33% of usage funds free AI for non‑profits you choose.' },
  ],
  cta: { label: 'See how it works', href: '#how-it-works' },
};